// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getShouldPreventCatalogLoop } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import loadBrandsAlphabet from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadBrandsAlphabet";
import { MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    overrideTitle = "overrideTitle",
}

const mapStateToProps = (state: ApplicationState) => {
    const allBrandsDataView = getAllBrandsDataView(state);

    return {
        shouldLoadBrandAlphabet:
            !state.pages.brands.brandAlphabetState.value && !state.pages.brands.brandAlphabetState.isLoading,
        shouldLoadAllBrandList: !allBrandsDataView.value && !allBrandsDataView.isLoading,
        deferredApiCalls: state.context.deferredApiCalls,
    };
};

const mapDispatchToProps = {
    loadBrandsAlphabet,
    loadAllBrands,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps &
    HasDeferredApiContext;

// XNG-Change
export interface BrandsPageStyles {
    page?: MobiusPageProps;
}

export const brandsPageStyles: BrandsPageStyles = {};

const styles = brandsPageStyles;

class BrandsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandsAlphabet, shouldLoadBrandAlphabet, shouldLoadAllBrandList, loadAllBrands } = this.props;
        if (shouldLoadBrandAlphabet && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadBrandsAlphabet();
        }
        if (shouldLoadAllBrandList && !getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            loadAllBrands({
                sort: "name asc",
                select: "id,detailPagePath,name",
            });
        }
    }

    render() {
        return (
            // XNG-Change
            <Page {...styles.page}>
                <Zone contentId={this.props.id} zoneName="Content" />
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(withIsInShell(BrandsPage))),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                displayName: "Page Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
        ],
    },
};

export default pageModule;

export const BrandsPageContext = "BrandsPage";
